import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Music2, VolumeX } from "lucide-react";

// Soft, license-free Celtic ambience for the magical world of Miara Mountain.
// Track: "Celtic Impulse" by Kevin MacLeod (incompetech.com) — CC BY 4.0.
const TRACK_SRC = "/audio/celtic.mp3";
const SOFT_VOLUME = 0.22;

const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const startPlayback = () => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = SOFT_VOLUME;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => setPlaying(true)).catch(() => {
        // Autoplay blocked — will retry on next user gesture
      });
    }
  };

  useEffect(() => {
    // Preferred trigger: the visitor clicking "Enter the Grotto"
    const onEnter = () => startPlayback();
    window.addEventListener("miara:enter", onEnter);

    // Fallback: begin softly on the very first interaction anywhere
    const onFirstGesture = () => {
      startPlayback();
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture);
    window.addEventListener("keydown", onFirstGesture);

    return () => {
      window.removeEventListener("miara:enter", onEnter);
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      startPlayback();
    }
  };

  return (
    <>
      <audio ref={audioRef} src={TRACK_SRC} loop preload="auto" />
      <motion.button
        onClick={toggle}
        data-testid="bg-music-toggle"
        aria-label={playing ? "Mute music" : "Play music"}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 left-6 z-40 w-12 h-12 rounded-full bg-emerald-950/60 backdrop-blur-md border border-amber-400/30 text-amber-200 flex items-center justify-center hover:border-amber-300/70 transition-colors shadow-lg"
      >
        {playing ? (
          <Music2 size={18} className="glow-pulse" />
        ) : (
          <VolumeX size={18} />
        )}
      </motion.button>
    </>
  );
};

export default BackgroundMusic;
